import React from 'react';
import 'twin.macro';
import { StaticImage } from 'gatsby-plugin-image';

import Layout from '~components/Layout';
import SEO from '~components/SEO';
import Container from '~components/Container';
import Hero from '~components/Hero';
import Heading from '~components/Heading';
import Callout from '~components/Callout';
import Text from '~components/Text';
import Blockquote from '~components/Blockquote';
import IconCallout from '~components/IconCallout';
import Female from '~components/Svg/Female';
import Handshake from '~components/Svg/Handshake';
import TableOfContents from '~components/TableOfContents';

import { fsbgimg } from '~theme';
import { ExternalLink } from '~components/Link';

const toc = [
  {
    id: 'coping-with-covid',
    title: 'Coping With COVID'
  },
  {
    id: 'microgrants',
    title: 'Microgrants'
  },
  {
    id: 'womens-program',
    title: 'Women’s Program'
  }
];

const CommunityPage: React.FC = () => (
  <Layout>
    <SEO title="Community Development" desc="" />
    <Hero title="Community Development">
      <StaticImage src="../images/group-of-women.jpg" alt="" style={fsbgimg} />
    </Hero>
    <Container>
      <TableOfContents items={toc} />
      <Heading id={toc[0].id} level="h2">
        <span tw="text-black">Coping With</span> <span tw="text-blue">COVID</span>
      </Heading>
      <Heading level="h3">"Yes I can!"</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Ruma overcame pandemic-strengthened barriers through the Coping with COVID project
        </Text>
      </Callout>
      <Text>
        Life during COVID-19 has been a struggle. For newcomer women living under the poverty line
        in the Taylor-Massey-Oakridge neighbourhood, the pandemic exacerbated the daunting
        socio-economic barriers they already faced. Thanks to funding from the United Way, that
        struggle was lessened: Together, we learned about Coping with COVID.
      </Text>
      <Text>
        Focusing on issues magnified by the pandemic, staff in the Coping with COVID project offered
        additional support, workshops, celebrations, advocacy, and referrals to counter language
        barriers and social services that functioned with severely reduced hours.
      </Text>
      <Text>
        We also published stories of happiness, struggle, and loss from some of these amazing women
        in <i>Coping With COVID: Stories of Resilience During COVID-19</i>. Here’s an excerpt from
        Ruma:
      </Text>
      <Blockquote>
        <Text tw="mt-0">
          “I came to Canada in 2019 to get treatment for my son Partha who suffers from epilepsy.
          COVID started soon after. I felt so isolated with only me and my son, and no where to go
          or other people to talk to.
        </Text>
        <Text>
          "I saw The Neighbourhood Group's Coping with COVID flyer and joined right away. I eagerly wait
          for the sessions where we talk and learn so many things. The conversation circle helps me
          speak with confidence and I’ve learned how to eat healthy for diabetes. We do yoga and
          anxiety management to reduce stress, and I now know where to go for a COVID test or
          vaccination, and how to get financial support during COVID.
        </Text>
        <Text tw="mb-0">
          "The program has also helped Partha. He’s met friends and is even volunteering here. He’s
          showing his courage and confidence. He’s turned, ‘How can I?’ into ‘Yes I can!’”
        </Text>
      </Blockquote>
      <IconCallout icon={<Female tw="w-full h-auto" />}>
        <Text>
          <strong>2000+</strong> interactions helped newcomer women access food security, isolation,
          education, primary and mental healthcare
        </Text>
      </IconCallout>
      <StaticImage src="../images/coping-with-covid-book.jpg" alt="" />
      <Heading id={toc[1].id} level="h2">
        Microgrants
      </Heading>
      <Heading level="h3">Small but powerful</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Microgrants help grassroots charitable organizations fulfill their mandates during
          COVID-19
        </Text>
      </Callout>
      <Text>
        For many small, grassroots charitable organizations, fulfilling their mandates with limited
        funding and administrative expertise is challenging. COVID-19 has turned those challenges
        into serious threats. Microgrants are an innovative way to sustain these essential
        organizations.
      </Text>
      <Text>
        Funded by the City of Toronto, the Microgrants project was developed to address gaps in
        emergency services and funding inequities for organizations that improve the lives of
        vulnerable people in our community. Microgrant recipients addressed the critical nutrition,
        hygiene, mental health and COVID-19 safety issues facing the people they serve.
      </Text>
      <Text>
        The Neighbourhood Group was proud to act as mentor and administrator for the following
        organizations:
      </Text>
      <ul tw="ml-3 list-inside list-disc text-lg md:(text-xl)">
        <li>Black Women in Motion</li>
        <li>Building Roots</li>
        <li>Equitable Action For Change</li>
        <li>Haven Toronto</li>
        <li>Healing As One</li>
        <li>S.E.A.S Centre</li>
        <li>Spadina/Fort York “Community Care”</li>
        <li>St. Luke’s United Church / Cabbagetown B.I.A. “Allen Gardens Celebrates”</li>
        <li>Youth Gravity</li>
      </ul>
      <Text>
        <StaticImage src="../images/sun-icon.jpg" alt="" />
      </Text>
      <Text>
        <ExternalLink to="https://www.equitablenow.ca/about">
          Recipient Spotlight: Equitable Action For Change
        </ExternalLink>
      </Text>
      <Text>
        To reduce the extremely high COVID-19 infection rate among people who use substances,
        Equitable Action For Change provided hygiene and protective supplies to people in this group
        who identify as Black and 2SLGBTQ+. The impressive package of supplies included:
      </Text>
      <ul tw="ml-3 list-inside list-disc text-lg md:(text-xl)">
        <li>1000+ face masks and shields</li>
        <li>10,000+ condoms</li>
        <li>Feminine hygiene products</li>
        <li>Wipes, gloves and hand sanitizer</li>
        <li>Toothpaste, toothbrushes and mouthwash</li>
        <li>Harm Reduction Care bags</li>
      </ul>
      <IconCallout icon={<Handshake tw="w-full h-auto" />}>
        <Text>
          Grassroots organizations received funding and administrative support to help them improve
          the lives of <strong>1600+</strong> marginalized people in our community
        </Text>
      </IconCallout>
      <Text>
        <div tw="grid gap-8 grid-cols-2 md:(grid-cols-3) lg:(grid-cols-4)">
          <StaticImage src="../images/logo-buildingroots.jpg" alt="" />
          <StaticImage src="../images/logo-bwim.jpg" alt="" />
          <StaticImage src="../images/logo-cabbagetown.jpg" alt="" />
          <StaticImage src="../images/logo-spafy.jpg" alt="" />
          <StaticImage src="../images/logo-hao.jpg" alt="" />
          <StaticImage src="../images/logo-seas.jpg" alt="" />
          <StaticImage src="../images/logo-youthgravity.jpg" alt="" />
          <StaticImage src="../images/logo-haven.jpg" alt="" />
        </div>
      </Text>
      <Heading id={toc[2].id} level="h2">
        Women’s Program
      </Heading>
      <Heading level="h3">My New Mother</Heading>
      <Callout>
        <Text tw="my-0 text-2xl">
          Safia Hirsi, Women’s Program Coordinator, shares a story of how the Program helps people
          by first building trust
        </Text>
      </Callout>
      <Blockquote>
        <Text tw="mt-0">
          “Darya moved to Canada with her son eight years ago. Recently, they weren’t able to manage
          the rent and an eviction notice followed. She contacted me for help, and fortunately her
          ODSP worker was able to arrange payments directly to Toronto Community Housing, allowing
          her to stay in her home. That was the start of our close relationship. And from then, she
          started calling me her daughter.
        </Text>
        <Text>
          “During the pandemic, I’ve been able to help Darya with her medications, and arranging for
          food delivery during Ramadan. Recently, I found out she was in the hospital after a fall.
          Darya was scared, alone, was struggling with language barriers with the nursing staff, and
          had not eaten as she had not been given Halal food.
        </Text>
        <Text>
          “When Humisha, one of the participants in the Women’s Program, heard that Darya had not
          eaten, she worked tirelessly to deliver tea and Halal snacks to her, despite an initial
          refusal to allow it. Humisha told me she learned in our Program to advocate for herself
          and for others. And it worked. The nurse also shared that Darya wasn’t taking the hospital
          medications because she wanted to take the ones she was used to. I explained the hospital
          policy to Darya and reassured her that the medicine was safe, and thankfully, she started
          taking it.
        </Text>
        <Text>
          “I also arranged with the nurses for Darya to be given Halal food and for her social worker
          and doctor to ensure she has the proper support when she returns home. That includes
          having a Personal Support Worker, safety features to be included in her home and access to
          transportation throughout her recovery.
        </Text>
        <Text tw="mb-0">
          “Darya now knows she is not alone. She knows someone will be there to answer when she
          calls and will support her through the difficult moments of her life.”
        </Text>
      </Blockquote>
    <Text>
      <StaticImage src="../images/group-of-women-on-camera.jpg" alt="" />
    </Text>
      <Text>
        <strong>336</strong> women from Bengali, Tamil, Mandarin, Swahili and Somali communities
        gain support and a network to help them with immigration, family, school system and
        referrals.
      </Text>
    </Container>
  </Layout>
);

export default CommunityPage;
